import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import FeatureSlide from "./FeatureSlide";
import { SLIDES, SECTION_COPY } from "./data";

const AUTO_PLAY_MS = 5000;
const SWIPE_THRESHOLD = 60;

const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 120 : -120, scale: 0.97 }),
  center: { opacity: 1, x: 0, scale: 1 },
  exit: (dir) => ({ opacity: 0, x: dir > 0 ? -120 : 120, scale: 0.97 }),
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
};

const FeatureShowcase = () => {
  const [[index, direction], setState] = useState([0, 0]);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  const count = SLIDES.length;
  const current = useMemo(() => SLIDES[index], [index]);

  const goTo = useCallback(
    (next) => {
      setState(([prev]) => {
        const wrapped = (next + count) % count;
        return [wrapped, wrapped > prev || (prev === count - 1 && wrapped === 0) ? 1 : -1];
      });
    },
    [count]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Pause auto-play when section is off-screen.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Auto-play.
  useEffect(() => {
    if (paused || !inView || reducedMotion) return undefined;
    const id = setTimeout(() => goTo(index + 1), AUTO_PLAY_MS);
    return () => clearTimeout(id);
  }, [index, paused, inView, reducedMotion, goTo]);

  const handleDragEnd = (_e, info) => {
    if (info.offset.x < -SWIPE_THRESHOLD) next();
    else if (info.offset.x > SWIPE_THRESHOLD) prev();
  };

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-black py-14 px-4 sm:py-20 md:py-28 md:px-8"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Gradient blobs (existing theme pattern) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        {/* Heading */}
        <motion.div
          className="mx-auto mb-8 max-w-3xl text-center sm:mb-10 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl md:text-3xl lg:text-4xl">
            {SECTION_COPY.heading}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-white/70 sm:mt-4 md:mt-5 md:text-base lg:text-lg">
            {SECTION_COPY.subheading}
          </p>
        </motion.div>

        {/* Carousel — natural height so text + demo never clip on mobile */}
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur sm:rounded-3xl">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

          <div className="relative min-w-0">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={current.id}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.18}
                onDragEnd={handleDragEnd}
                className="min-w-0 cursor-grab active:cursor-grabbing"
              >
                <FeatureSlide slide={current} active />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Prev / Next — visible from sm up; dots cover mobile */}
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-2 top-1/2 z-20 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/50 text-white/80 backdrop-blur transition-colors hover:bg-black/70 hover:text-white sm:flex md:left-3 md:h-10 md:w-10"
          >
            <FaChevronLeft className="text-sm" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="absolute right-2 top-1/2 z-20 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/50 text-white/80 backdrop-blur transition-colors hover:bg-black/70 hover:text-white sm:flex md:right-3 md:h-10 md:w-10"
          >
            <FaChevronRight className="text-sm" />
          </button>
        </div>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-2 sm:mt-8 sm:gap-2.5">
          {SLIDES.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to ${slide.title}`}
              className="group relative h-2 rounded-full transition-all duration-300 sm:h-2.5"
              style={{ width: i === index ? 28 : 8 }}
            >
              <span
                className={`block h-full w-full rounded-full transition-colors ${
                  i === index
                    ? "bg-gradient-to-r from-orange-500 to-amber-500"
                    : "bg-white/20 group-hover:bg-white/40"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
